import { pendingRaycastReturn } from '../ExtensionAuth/raycastAuth';
import { setAuthToken, clearAuthTokens } from '../../../utils/authSession';
import '../../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useState, useEffect, useRef } from 'react';

import { MUTATION } from '../../../gql-operations';
import { Link } from 'react-router-dom';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { withRouter, useParams } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';
import { IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
import { FaApple } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';

import customAnalytics from '../../../util/custom-analytics';
import AuthLayout from '../AuthLayout';
import { consumePendingPublishedFolderCopy } from '../../../util/pendingPublishedFolderCopy';
import {
    consumePendingSocialAuthMethod,
    useLastUsedAuthMethod,
    rememberLastUsedAuthMethod,
    rememberPendingSocialAuthMethod
} from '../../../util/lastAuthMethod';
import environment from '../../../env-config';

function Login({ history, location, match }) {
    const {t: lnT} = useTranslation();

    // console.log('loggin the match', location.state);
    const [loginResponse, setLoginResponse] = useState(undefined);
    const [showPassword, setShowPassword] = useState(false);
    const [socialLoginError, setSocialLoginError] = useState('');
    const [socialProviders, setSocialProviders] = useState({ google: false, apple: false });
    const [lastUsedAuthMethod, setLastUsedAuthMethod] = useLastUsedAuthMethod();
    const socialCompletionCodeRef = useRef('');
    const apolloClient = useApolloClient();
    const [login, { loading }] = useMutation(MUTATION.LOGIN, {
        onCompleted: res => {
            setLoginResponse(res);
            // Successful login analytics runs after storing the session.
        }
    });

    useEffect(() => {
        let cancelled = false;
        const apiBaseUrl = String(environment.REACT_BACKEND_URL || '').replace(/\/graphql\/?$/, '');
        if (!apiBaseUrl) return undefined;

        fetch(`${apiBaseUrl}/api/auth/providers`, { cache: 'no-store' })
            .then(response => (response.ok ? response.json() : {}))
            .then(providers => {
                if (!cancelled) {
                    setSocialProviders({
                        google: providers?.google === true,
                        apple: providers?.apple === true
                    });
                }
            })
            .catch(() => {
                if (!cancelled) setSocialProviders({ google: false, apple: false });
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const startSocialSignIn = provider => {
        const apiBaseUrl = String(environment.REACT_BACKEND_URL || '').replace(/\/graphql\/?$/, '');
        if (!apiBaseUrl) {
            setSocialLoginError(lnT('auth:socialSignInIsUnavailableBecauseThe'));
            return;
        }
        rememberPendingSocialAuthMethod(provider);
        const requestedRedirect = new URLSearchParams(location.search).get('redirect');
        const returnTo = requestedRedirect && requestedRedirect.startsWith('/') && !requestedRedirect.startsWith('//')
            ? requestedRedirect
            : '/home';
        window.location.assign(
            `${apiBaseUrl}/api/auth/${provider}/start?returnTo=${encodeURIComponent(returnTo)}`
        );
    };

    const _setLocalStorage = response => {
        const { login } = response;

        if (login && login.token && login.refreshToken) {
            try {
                console.log('SETTING LOCAL STORAGE');
                const { token, refreshToken, userData } = login;
                setAuthToken(token, { refreshToken });
                customAnalytics.login(login);
                // localStorage.setItem('userData', JSON.stringify(userData));
            } catch (error) {
                console.error('Failed to decode token:', error.message);
                // Clear invalid tokens
                clearAuthTokens();
            }
        }
    };

    // Handle successful login - move side effects out of render
    useEffect(() => {
        const finishLogin = async () => {
            rememberLastUsedAuthMethod('email');
            setLastUsedAuthMethod('email');
            _setLocalStorage(loginResponse);
            setLoginResponse(undefined);

            const raycastReturn = pendingRaycastReturn(location.search);
            if (raycastReturn) { history.push(raycastReturn); return; }

            try {
                const copiedFolder = await consumePendingPublishedFolderCopy(apolloClient);
                if (copiedFolder?.id) {
                    history.push(`/f/${copiedFolder.id}`);
                    return;
                }
            } catch (error) {
                console.error('Failed to copy pending published folder:', error);
            }

            const redirect = new URLSearchParams(location.search).get('redirect');
            history.push(redirect || '/home');
        };

        if (loginResponse && loginResponse.login && loginResponse.login.__typename === 'AuthPayload') {
            finishLogin();
        }
    }, [apolloClient, loginResponse, history, location.search]);

    // Google/Apple callbacks redirect here with a short-lived opaque code in
    // the URL fragment, never a Luckynote JWT. Fragments are not sent in HTTP
    // requests. Exchange it once, remove it from the address bar, and then use
    // the same local session storage as password sign-in.
    useEffect(() => {
        const code = new URLSearchParams(String(location.hash || '').replace(/^#/, '')).get('social_code');
        if (!code || socialCompletionCodeRef.current === code) return;

        socialCompletionCodeRef.current = code;
        history.replace({
            pathname: location.pathname,
            search: location.search,
            hash: ''
        });

        const exchange = async () => {
            const apiBaseUrl = String(environment.REACT_BACKEND_URL || '').replace(/\/graphql\/?$/, '');
            if (!apiBaseUrl) {
                setSocialLoginError(lnT('auth:socialSignInIsUnavailableBecauseThe'));
                return;
            }

            try {
                const response = await fetch(`${apiBaseUrl}/api/auth/session/exchange`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ code })
                });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok || !payload.token || !payload.refreshToken) {
                    setSocialLoginError(lnT('auth:thisSignInLinkExpiredPleaseTry'));
                    return;
                }

                const provider = consumePendingSocialAuthMethod();
                if (provider) {
                    rememberLastUsedAuthMethod(provider);
                    setLastUsedAuthMethod(provider);
                }
                _setLocalStorage({ login: payload });
                const raycastReturn = pendingRaycastReturn(location.search);
                if (raycastReturn) { history.push(raycastReturn); return; }
                const copiedFolder = await consumePendingPublishedFolderCopy(apolloClient);
                if (copiedFolder?.id) {
                    history.push(`/f/${copiedFolder.id}`);
                    return;
                }

                const redirect = new URLSearchParams(location.search).get('redirect');
                history.push(redirect || '/home');
            } catch (error) {
                setSocialLoginError(lnT('auth:weCouldNotCompleteSocialSignIn'));
            }
        };

        exchange();
    }, [apolloClient, history, location.hash, location.pathname, location.search]);

    // const [forgotResponse, setForgotResponse] = useState(false);

    // useEffect(() => {
    // 	if (location.state?.forgotResponse) {
    // 		// forgotResponse updated, do something with `route.params.post`
    // 		// For example, send the post to the server
    // 		const { forgotResponse } = location.state;
    // 		console.log('i get this', forgotResponse);
    // 		if (forgotResponse && forgotResponse.data.forgotPasswordMail) {
    // 			setForgotResponse(true);
    // 		} else {
    // 			// TODO maybe display some error
    // 		}
    // 	}
    // 	return () => {
    // 		setForgotResponse(false);
    // 		location.state = {}
    // 	};
    // }, [location.state?.forgotResponse]);

    // const renderSuccessfulForgetResponse = () => {
    // 	if (forgotResponse) {
    // 		return (
    // 			<ErrorText>
    // 				The email has been sent, please check your inbox
    // 			</ErrorText>
    // 		);
    // 	}
    // };

    const renderResponseMessage = () => {
        if (loginResponse && loginResponse.login) {
            switch (loginResponse.login.__typename) {
                case 'AuthPayload':
                    // Success case is handled in useEffect, just return null here
                    return null;
                case 'LoginInvalidInputError':
                case 'UserNotFound':
                case 'PasswordsDoNotMatch':
                case 'UserLocked':
                    return (
                        <div className="auth-alert auth-alert--error">
                            <span className="auth-alert-icon">⚠</span>
                            <span className="auth-alert-body">{loginResponse.login.message}</span>
                        </div>
                    );
                case 'UserNotVerified':
                    return (
                        <div className="auth-alert auth-alert--warning">
                            <span className="auth-alert-icon">✉</span>
                            <span className="auth-alert-body">
                                {loginResponse.login.message}{' '}
                                <Link to={'/check-account'}>{lnT('settings:verifyAccount')}</Link>
                            </span>
                        </div>
                    );
                case 'InternalServerError':
                    return (
                        <div className="auth-alert auth-alert--error">
                            <span className="auth-alert-icon">⚠</span>
                            <span className="auth-alert-body">{lnT('auth:serverErrorWeLlBeBackSoon')}</span>
                        </div>
                    );
                default:
                    return (
                        <div className="auth-alert auth-alert--error">
                            <span className="auth-alert-icon">⚠</span>
                            <span className="auth-alert-body">{lnT('common:unknownError')}</span>
                        </div>
                    );
            }
        }
        return null;
    };

    return (
        <AuthLayout>
            <Formik
                initialValues={{
                    email: '',
                    password: ''
                }}
                onSubmit={async (values, actions) => {
                    await setLoginResponse(undefined);
                    login({
                        variables: {
                            data: {
                                email: values.email,
                                password: values.password,
                                settings: {
                                    usedDesktopWebApp: true
                                }
                            }
                        }
                    });
                    // just checking before letting you in
                    actions.setSubmitting(false);
                }}
                validationSchema={Yup.object().shape({
                    email: Yup.string().email(lnT('auth:invalidEmail')).required(lnT('auth:required')),
                    password: Yup.string().min(6, lnT('auth:minimumPasswordLengthIs6')).max(50, lnT('auth:maximumPasswordLengthIs50')).required(lnT('auth:required'))
                })}
            >
                {({ values, handleSubmit, handleChange, handleBlur, errors, touched }) => (
                    <>
                        <h1 className="auth-title">{lnT('auth:welcomeBack_66212495')}</h1>
                        <p className="auth-subtitle">{lnT('auth:signInToKeepCapturingYourNotes')}</p>

                        {renderResponseMessage()}

                        {socialLoginError ? (
                            <div className="auth-alert auth-alert--error">
                                <span className="auth-alert-icon">⚠</span>
                                <span className="auth-alert-body">{socialLoginError}</span>
                            </div>
                        ) : null}

                        {(socialProviders.google || socialProviders.apple) ? (
                            <>
                                <div className="auth-social-buttons">
                                    {socialProviders.google ? (
                                        <button
                                            type="button"
                                            className="auth-social-button"
                                            onClick={() => startSocialSignIn('google')}
                                        >
                                            <FcGoogle className="auth-social-icon" aria-hidden="true" />{lnT('auth:continueWithGoogle')}{lastUsedAuthMethod === 'google' ? <span className="auth-last-used-badge">{lnT('auth:lastUsed')}</span> : null}
                                        </button>
                                    ) : null}
                                    {socialProviders.apple ? (
                                        <button
                                            type="button"
                                            className="auth-social-button"
                                            onClick={() => startSocialSignIn('apple')}
                                        >
                                            <FaApple className="auth-social-icon auth-social-icon--apple" aria-hidden="true" />{lnT('auth:continueWithApple')}{lastUsedAuthMethod === 'apple' ? <span className="auth-last-used-badge">{lnT('auth:lastUsed')}</span> : null}
                                        </button>
                                    ) : null}
                                </div>
                                <div className="auth-or-divider" aria-hidden="true"><span>{lnT('auth:or')}</span></div>
                            </>
                        ) : null}

                        <form onSubmit={handleSubmit} className="auth-form">
                            <div className="auth-field">
                                <input
                                    name="email"
                                    type="email"
                                    onChange={handleChange('email')}
                                    value={values.email}
                                    placeholder={lnT('common:email')}
                                    spellCheck="off"
                                    autoCorrect="off"
                                />
                                <ErrorMessage
                                    name="email"
                                    render={msg => (
                                        <div id="email" className="error">
                                            {msg}
                                        </div>
                                    )}
                                />
                            </div>

                            <div className="auth-field">
                                <div className="auth-field-input-wrap">
                                    <input
                                        name="password"
                                        type={showPassword ? "text" : "password"}
                                        onChange={handleChange('password')}
                                        value={values.password}
                                        placeholder={lnT('common:password')}
                                        autoCapitalize="off"
                                        autoCorrect="off"
                                        style={{ paddingRight: 44 }}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
                                    </button>
                                </div>
                                <ErrorMessage
                                    name="password"
                                    render={msg => (
                                        <div id="password" className="error">
                                            {msg}
                                        </div>
                                    )}
                                />
                            </div>

                            <button type="submit" className="auth-submit">
                                {loading ? lnT('auth:signingIn_1f96f5d0') : lnT('auth:signIn')}
                                {!loading && lastUsedAuthMethod === 'email' ? <span className="auth-last-used-badge">{lnT('auth:lastUsed')}</span> : null}
                            </button>
                        </form>

                        <div className="auth-link-row">
                            <Link to={'/forgot-password'}>{lnT('auth:forgotPassword')}</Link>
                        </div>

                        <hr className="auth-divider" />

                        <div className="auth-footer">{lnT('auth:donTHaveAnAccount')}<Link to={'/register'}>{lnT('onboarding:createAccount')}</Link>
                        </div>
                    </>
                )}
            </Formik>
        </AuthLayout>
    );
}

export default withRouter(Login);
